/* @flow */

import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import styles from './AuthenticationBase.style';

import AuthenticationFormHeader from './AuthenticationFormHeader';
import LazyLoginSupportMain from 'src/shared/app/authentication/component/support/login/LazyLoginSupportMain';
import messages from 'src/shared/app/authentication/l10n/authenticationL10n';
import { usernameFieldMaxLength } from 'src/shared/app/authentication/settings/authenticationSettings';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import Markdown from 'src/shared/app/base/component/text/Markdown';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { loginSupportRoute } from 'src/shared/app/base/route/setting/routeSettings';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';
import NoteText from 'src/shared/ui/component/text/NoteText';

type Props = {
  type: ?string,
  initialUsername: ?string,
  legend: ?string,
  label: ?string,
  placeholder?: string,
  note: ?string,
  submitCta: string,
  handleSubmit: (Object) => Promise<any>,
};

const useStyles = createUseStyles(styles);

const AuthenticationUsernameForm = ({
  type: propType,
  initialUsername,
  legend,
  label,
  placeholder,
  note,
  submitCta,
  handleSubmit,
}: Props): React.Node => {
  const type = propType || 'email';

  let usernameSchema = Yup.string().required();

  if (type === 'email') {
    usernameSchema = usernameSchema.email();
  }

  const schema = Yup.object().shape({
    username: usernameSchema,
  });

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const [loginSupportPath] = useRoutes(loginSupportRoute);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    LazyLoginSupportMain.preload();
  }, []);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const legendId = 'username-desc';

  const noteElt = note && (
    <NoteText layoutClass={classes.footer}>
      <Markdown>{note}</Markdown>
    </NoteText>
  );

  return (
    <Formik
      initialValues={{ username: initialUsername }}
      validationSchema={schema}
      validateOnChange={false}
      onSubmit={(values, { setSubmitting, setStatus }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          const { root, username } = errors || {};

          if (root || username) {
            setStatus(root || username);
          }
        })
      }
    >
      {({ status, isSubmitting, values }) => (
        <Form className={classes.form} noValidate>
          <AuthenticationFormHeader
            id={legend ? legendId : ''}
            withError={!!status}
          >
            {status || legend}
          </AuthenticationFormHeader>
          <Field
            id="username"
            name="username"
            type={type}
            component={FormikFormGroup}
            componentOpts={{
              label: label || intl.formatMessage(messages.emailLabel),
            }}
            maxLength={usernameFieldMaxLength}
            placeholder={placeholder}
            required
            autoFocus
            aria-describedby={legendId}
          />
          <div className={classes.submit}>
            <SecondaryButton
              isDisabled={!values.username}
              isSubmitting={isSubmitting}
            >
              {submitCta}
            </SecondaryButton>
          </div>
          <div className={classes.support}>
            <DiscreteLink href={loginSupportPath}>
              {intl.formatMessage(messages.troubleLoggingIn)}
            </DiscreteLink>
          </div>
          {noteElt}
        </Form>
      )}
    </Formik>
  );
};

export default compose(React.memo)(AuthenticationUsernameForm);
